import React from "react";
import dayjs from "dayjs";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { SortType } from "../../../../../models/SortType";
import { StorageCostDetailItem } from "../../../../../models/StorageCost/StorageCost";
import {
  getModernPageColors,
  modernEmptyStateSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTablePaperSx,
  modernTableRowSx,
} from "../../../../../styles/modernListingPageSx";

interface StorageCostDetailsListingTableProps {
  detailRows: StorageCostDetailItem[];
  isLoading: boolean;
  sortBy: string;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const formatDate = (value?: string) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM, YYYY") : value;
};

const StorageCostDetailsListingTable: React.FC<
  StorageCostDetailsListingTableProps
> = ({ detailRows, isLoading, sortBy, sortType, onSortChange }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "25vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (detailRows.length === 0) {
    return (
      <Paper sx={modernTablePaperSx(theme)}>
        <Box sx={{ ...modernEmptyStateSx(theme), minHeight: "10rem" }}>
          <CustomTypography variant="heading6" color={colors.subtitle}>
            No storage cost rows for this range.
          </CustomTypography>
        </Box>
      </Paper>
    );
  }

  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {detailRows.map((row, index) => (
          <Paper
            key={`${row.date || "row"}-${row.label || ""}-${index}`}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${colors.border}`,
              bgcolor: colors.surface,
              boxShadow: colors.shadowSoft,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                mb: 1,
              }}
            >
              <CustomTypography variant="heading7" color={colors.title}>
                {row.label || "Pack"}
              </CustomTypography>
              <CustomTypography variant="heading8" color={colors.accent}>
                {formatCurrency(row.total_cost_per_day)}
              </CustomTypography>
            </Box>
            <CustomTypography variant="body2" color={colors.subtitle}>
              {formatDate(row.date)}
              {row.partner_tag ? ` · ${row.partner_tag}` : ""}
            </CustomTypography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                mt: 1.25,
              }}
            >
              <DetailStat label="Size" value={row.size || "-"} />
              <DetailStat label="Pack size" value={String(row.pack_size || "-")} />
              <DetailStat label="Box size" value={row.box_size || "-"} />
              <DetailStat label="Quantity" value={String(row.quantity ?? "-")} />
              <DetailStat
                label="Monthly cost"
                value={formatCurrency(row.monthly_storage_cost)}
              />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <Paper sx={modernTablePaperSx(theme)}>
      <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell title="Sr No" sx={modernTableHeaderCellSx(theme)} />
              <AppTableHeaderCell
                title="Date"
                sortColumn="date"
                sortBy={sortBy}
                sortDirection={sortType}
                onSortChange={onSortChange}
                sx={modernTableHeaderCellSx(theme)}
              />
              <AppTableHeaderCell
                title="Partner Tag"
                sx={modernTableHeaderCellSx(theme)}
              />
              <AppTableHeaderCell
                title="Label"
                sx={{
                  ...modernTableHeaderCellSx(theme),
                  minWidth: 180,
                  textAlign: "left",
                }}
              />
              <AppTableHeaderCell title="Size" sx={modernTableHeaderCellSx(theme)} />
              <AppTableHeaderCell
                title="Pack Size"
                sx={modernTableHeaderCellSx(theme)}
              />
              <AppTableHeaderCell
                title="Box Size"
                sx={modernTableHeaderCellSx(theme)}
              />
              <AppTableHeaderCell
                title="Quantity"
                sx={modernTableHeaderCellSx(theme)}
              />
              <AppTableHeaderCell
                title="Monthly Cost"
                sx={modernTableHeaderCellSx(theme)}
              />
              <AppTableHeaderCell
                title="Total / Day"
                sx={modernTableHeaderCellSx(theme)}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {detailRows.map((row, index) => (
              <TableRow
                key={`${row.date || "row"}-${row.label || ""}-${index}`}
                hover
                sx={modernTableRowSx(theme)}
              >
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {index + 1}
                </AppTableRowCell>
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {formatDate(row.date)}
                </AppTableRowCell>
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {row.partner_tag || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    ...modernTableBodyCellSx(theme),
                    textAlign: "left",
                    minWidth: 180,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {row.label || "-"}
                </AppTableRowCell>
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {row.size || "-"}
                </AppTableRowCell>
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {row.pack_size || "-"}
                </AppTableRowCell>
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {row.box_size || "-"}
                </AppTableRowCell>
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {row.quantity}
                </AppTableRowCell>
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {formatCurrency(row.monthly_storage_cost)}
                </AppTableRowCell>
                <AppTableRowCell sx={modernTableBodyCellSx(theme)}>
                  {formatCurrency(row.total_cost_per_day)}
                </AppTableRowCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const DetailStat: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  return (
    <Box>
      <CustomTypography variant="heading9" color={colors.subtitle}>
        {label}
      </CustomTypography>
      <CustomTypography variant="body2" color={colors.title}>
        {value}
      </CustomTypography>
    </Box>
  );
};

export default StorageCostDetailsListingTable;
